import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, input, output, ChangeDetectionStrategy } from '@angular/core';
import { UserRights } from '@maximizer/core/shared/domain';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { UI_PIPES } from '@maximizer/core/shared/ui/pipes';
import {
  EntryLinkService,
  OutlookStore,
  VerticalService,
} from '@maximizer/outlook/shared/data-access';
import { CaseCard } from '@maximizer/outlook/shared/domain';
import { OUTLOOK_SHARED_UI } from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';

@Component({
  selector: 'maximizer-outlook-case-card',
  templateUrl: './case-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    KENDO_BUTTON,
    KENDO_TOOLTIP,
    NgTemplateOutlet,
    OUTLOOK_SHARED_UI,
    TranslatePipe,
    UI_KENDO,
    UI_PIPES,
  ],
})
export class CaseCardComponent {
  save = output<string>();

  userRights = input<UserRights>();
  mode = input<'read' | 'compose'>('read');
  case = input<CaseCard>({
    key: '',
    subject: '',
    parentKey: '',
    status: null,
    priority: null,
    caseNumber: '',
    abEntry: {
      key: null,
      companyName: '',
    },
    owner: {
      value: '',
      display: '',
    },
    assignedTo: null,
    isSaved: false,
    contact: {
      key: null,
      firstName: '',
      lastName: '',
    },
  });

  deepLink = computed(() =>
    this.entryLinkService.buildEntryLink(this.case().key, 'case'),
  );

  constructor(
    private readonly translate: TranslateService,
    private readonly verticalService: VerticalService,
    private readonly entryLinkService: EntryLinkService,
    public outlookStore: OutlookStore,
  ) {}

  // Compose saves the current draft, so the same case can be saved again
  get isSaveDisabled(): boolean {
    return (
      (this.mode() === 'read' && this.case().isSaved) ||
      !this.userRights()?.customerService?.update ||
      !this.userRights()?.documents?.insert
    );
  }

  get saveIconClass(): string {
    if (this.case().isSaved) {
      return this.mode() === 'compose'
        ? 'maximizer-icons icon-saved-email icon-20'
        : 'svg-icons icon-saved-email text-neutral-40 icon-20';
    }

    return this.isSaveDisabled
      ? 'maximizer-icons icon-save-email text-neutral-40 icon-24'
      : 'maximizer-icons icon-save-email icon-24';
  }

  get saveTooltipMessage(): string {
    if (this.case().isSaved && this.mode() === 'read') {
      return this.translate.instant('outlook.main.tooltip.emailSavedAlt');
    }

    if (!this.userRights()?.customerService?.update) {
      return this.translate.instant('outlook.rights.defaultMessage', {
        right: this.translate.instant('outlook.rights.edit'),
        entry: this.verticalService.translateVertical('cases'),
      });
    }

    if (!this.userRights()?.documents?.insert) {
      return this.translate.instant('outlook.rights.defaultMessage', {
        right: this.translate.instant('outlook.rights.create'),
        entry: this.translate
          .instant('outlook.entries.emailLogs')
          .toLowerCase(),
      });
    }

    return this.translate.instant('outlook.main.related.saveEmail', {
      entry: this.verticalService.translateVertical('case'),
    });
  }
}
